import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'

import CampaignEditForm from '../../../../../ReusableFormSections/reusableBudgetSections/CampaignEditForm'
import RemainingCampaignImpressionsBudgetFields from '../../../../UploadAdCreative/ImpressionsBudgetSection/RemainingCampaignImpressionsBudgetFields'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import {
  calculateBudgetFromImpressions,
  calculateImpressionsFromBudget
} from '../../../../UploadAdCreative/ImpressionsBudgetSection/helpers'
import { calc } from '../../../../../../helpers/numbers'

import { updateCampaign } from '../../../../../../modules/actions/campaigns'
import { rightSelector } from '../../../../../../modules/selectors/rights'
import { lifetimeDatePresetCombinedCampaignSelector } from '../../../../../../modules/selectors/combinedData'

import { IMPRESSIONS } from '../../../../UploadAdCreative/fields'
import { BUDGET_LIFETIME, OBJECTIVE } from '../../../../CampaignForms/fields'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import { getImpressionsValidationsSchema } from '../../../../../ReusableFormSections/reusableBudgetSections/remainingImpressionsValidation'

const CampaignImpressionBudgetForm = ({
  platform,
  adAccountId,
  campaign = {},
  title,
  remainingImpressions,
  editItemData,
  updateOptions,
  ...formProps
}) => {
  const dispatch = useDispatch()

  const right = useSelector(rightSelector)
  const { bid_cap: rightBidCap } = right

  const { [OBJECTIVE]: objective } = campaign

  const lifetimeDatePresetSelectedCampaign = useSelector(lifetimeDatePresetCombinedCampaignSelector(campaign?.id))
  const lifetimeDatePresetSelectedCampaignSpend = lifetimeDatePresetSelectedCampaign?.spend || 0

  const calculatedImpressions = useMemo(() => {
    // calculated_impressions is calculated by the (spend(with maximum date range filter on)/bid cap on right)*1000
    return calc(lifetimeDatePresetSelectedCampaignSpend).div(rightBidCap).mul(1000).toDP(0).toNumber()
  }, [lifetimeDatePresetSelectedCampaignSpend, rightBidCap])

  const handleSubmit = useCallback(
    values => {
      const calculatedBudgetFromImpressions = calculateBudgetFromImpressions({
        impressions: values[IMPRESSIONS],
        rightBidCap
      })

      const updateCampaignData = {
        account: adAccountId,
        // todo: support daily budget
        // ...(budgetType === BUDGET_LIFETIME && { budget_lifetime }),
        // ...(budgetType === BUDGET_DAILY && { budget_daily }),
        [BUDGET_LIFETIME]: calculatedBudgetFromImpressions,
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(updateCampaign(updateCampaignData, campaign.id, platform, updateOptions))
    },
    [dispatch, title, campaign.id, platform, adAccountId, updateOptions, rightBidCap]
  )

  const initialCampaignImpressions = calculateImpressionsFromBudget({
    budget: campaign?.[BUDGET_LIFETIME],
    rightBidCap
  })

  const initialValues = useMemo(
    () => ({
      // todo support daily budget
      // [BUDGET_LIFETIME]: (campaign && String(campaign.budget_lifetime)) || '',
      // [BUDGET_DAILY]: (campaign && String(campaign.budget_daily)) || ''
      [IMPRESSIONS]: initialCampaignImpressions || ''
    }),
    [initialCampaignImpressions]
  )

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: getImpressionsValidationsSchema(
      remainingImpressions,
      initialCampaignImpressions,
      calculatedImpressions
    )
  })

  const purifiedFormik = usePurifiedFormik(formik)

  return (
    <CampaignEditForm formik={purifiedFormik} initialValues={initialValues} {...formProps}>
      <RemainingCampaignImpressionsBudgetFields
        formik={formik}
        remainingImpressions={remainingImpressions}
        calculatedImpressions={calculatedImpressions}
        objective={objective}
      />
    </CampaignEditForm>
  )
}

CampaignImpressionBudgetForm.propTypes = {
  platform: PropTypes.string.isRequired,
  adAccountId: PropTypes.string.isRequired,
  remainingImpressions: PropTypes.number.isRequired,
  campaign: PropTypes.object.isRequired,
  title: PropTypes.string,
  editItemData: PropTypes.object,
  updateOptions: PropTypes.object
}

export default CampaignImpressionBudgetForm
